import { Button } from "primereact/button"
import { useCallback, useEffect, useState } from "react"
import { Dropdown } from "primereact/dropdown"
import { Calendar } from "primereact/calendar"
import { Paginator } from "primereact/paginator"
import { MultiSelect } from "primereact/multiselect"
import FilterDialogMobile from "./FilterDialogMobile"
import { formatToLocalDate } from "../../helpers/dateConversion"
import { DateTime } from "luxon"
import NoData from "../NoData"
import { InputText } from "primereact/inputtext"
import { IconField } from "primereact/iconfield"
import { InputIcon } from "primereact/inputicon"
import { classNames } from "primereact/utils"

//Styles
import customTableStyles from "../../styles/customTable/CustomTable.module.css"
import customSortStyles from "../../styles/CustomSort.module.css"

const CustomTable = ({
  filters,
  collection,
  sorting,
  headers,
  styles,
  tableRowStyles,
  dataStyles,
  actionStyles,
  maxRows,
  title,
  disablePagination,
  singleSortOnly,
}) => {
  const [data, setData] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [first, setFirst] = useState(0)
  const [rows, setRows] = useState(maxRows ?? 10)
  const [activeFilters, setActiveFilters] = useState({})
  const [activeSorting, setActiveSorting] = useState({})
  const [showMobileFilters, setShowMobileFilters] = useState(false)
  const [showClearFilters, setShowClearFilters] = useState(false)
  const [calendarValue, setCalendarValue] = useState(null)

  useEffect(() => {
    if (collection) {
      setData(collection)
      setFilteredData(collection)
    }
  }, [collection])

  useEffect(() => {
    if (filters) {
      const _filters = filters.reduce((acc, filter) => {
        // if (
        //   filter.type === "dropdown" &&
        //   filter.options &&
        //   filter.options.length === 1
        // ) {
        //   acc[filter.value] = filter.options[0]
        // }
        // Note: This code is commented out to avoid setting a default value for dropdowns with a single option
        if (filter.default) {
          acc[filter.value] = filter.default
        }
        return acc
      }, {})

      setActiveFilters({ ..._filters })
    }
  }, [filters])

  useEffect(() => {
    if (sorting && sorting[0]?.default && collection?.length > 0) {
      if (Array.isArray(sorting[0]?.default)) {
        // If the "default" sort is an array of values

        let sortOrder = {}
        if (Array.isArray(sorting[0]?.defaultOrder)) {
          // defaultOrder is an array of boolean values
          let index = 0
          sortOrder = sorting[0]?.default
            .map((s) => ({
              [s]: sorting[0]?.defaultOrder[index++] || false, // default to descending if not specified
            }))
            .reduce((acc, curr) => ({ ...acc, ...curr }), {})
        } else {
          // defaultOrder is a single boolean value
          sortOrder = sorting[0]?.default
            .map((s) => ({
              [s]: sorting[0]?.defaultOrder || false, // default to descending if not specified
            }))
            .reduce((acc, curr) => ({ ...acc, ...curr }), {})
        }

        if (
          !activeSorting?.sortBy?.find((s) => sorting[0]?.default.includes(s))
        ) {
          const updatedSort = {
            ...activeSorting,
            sortBy: [...(activeSorting.sortBy ?? []), ...sorting[0]?.default],
            sortType: { ...(activeSorting.sortType ?? {}), ...sortOrder },
          }
          setActiveSorting(updatedSort)
        }
      } else {
        // If the "default" sort is a single value
        if (!activeSorting?.sortBy?.find((s) => s === sorting[0]?.default)) {
          const updatedSort = {
            ...activeSorting,
            sortBy: [...(activeSorting.sortBy ?? []), sorting[0]?.default],
            sortType: {
              ...(activeSorting.sortType ?? {}),
              [sorting[0]?.default]: sorting[0]?.defaultOrder || false, // default to descending
            },
          }
          setActiveSorting(updatedSort)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting])

  const getSortItem = (a, b, key) => {
    let valueA = a[key]
    let valueB = b[key]

    if (typeof a[key] === "string") {
      valueA = a[key].toLowerCase()
    }
    if (typeof b[key] === "string") {
      valueB = b[key].toLowerCase()
    }
    if (a[key]?.props) {
      valueA = a[key]?.props?.children
    }
    if (b[key]?.props) {
      valueB = b[key]?.props?.children
    }
    return { valueA, valueB }
  }

  const isValidStringFormatDate = (dateStr) => {
    if (!dateStr) return null
    const formats = ["M/d/yyyy", "d/M/yyyy"]
    for (const format of formats) {
      const date = DateTime.fromFormat(dateStr, format)
      if (date.isValid) return date
    }
    return null
  }

  const isValidDate = (value) => {
    const dateTime = DateTime.fromISO(value)
    return dateTime.isValid
  }

  const sortByKey = (array, key, ascending) => {
    return array.sort((a, b) => {
      const values = getSortItem(a, b, key)
      let valueA = values.valueA
      let valueB = values.valueB
      let valueADate = isValidStringFormatDate(valueA)
      let valueBDate = isValidStringFormatDate(valueB)
      if (valueADate && valueBDate) {
        valueA = valueADate.toMillis()
        valueB = valueBDate.toMillis()
      }
      if (valueA < valueB) return ascending ? -1 : 1
      if (valueA > valueB) return ascending ? 1 : -1
      return 0
    })
  }

  const filterData = useCallback(() => {
    const _filters = Object.entries(activeFilters)
      .filter(([key, value]) => {
        if (Array.isArray(value)) {
          return value.length > 0
        } else if (value !== undefined) {
          return value
        }
        return false
      })
      ?.map(([key]) => key)

    let filtered = [...data]

    const search = activeFilters?.search
    if (search) {
      filtered = filtered.filter((fd) => {
        return Object.values(fd).some((v) => {
          if (typeof v === "string") {
            return v.toLowerCase().includes(search.toLowerCase())
          }
          return false
        })
      })
    }

    _filters
      ?.filter((f) => f !== "search")
      .forEach((filter) => {
        if (isValidDate(activeFilters[filter])) {
          filtered = filtered.filter(
            (fd) => fd[filter] === formatToLocalDate(activeFilters[filter]),
          )
        } else if (Array.isArray(activeFilters[filter])) {
          filtered = filtered.filter((fd) =>
            activeFilters[filter]?.includes(fd[filter]),
          )
        } else {
          filtered = filtered.filter(
            (fd) => fd[filter] === activeFilters[filter],
          )
        }
      })

    if (activeSorting?.sortBy?.length > 0 && filtered.length > 0) {
      let sortedData = [...filtered]
      activeSorting?.sortBy?.forEach((f) => {
        sortedData = sortByKey(sortedData, f, activeSorting.sortType?.[f])
      })
      filtered = sortedData
    }

    const hasActiveFilters = Object.keys(activeFilters).length > 0
    setShowClearFilters(hasActiveFilters)
    setFilteredData(filtered)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters, data, activeSorting])

  useEffect(() => {
    if (!showMobileFilters) {
      filterData()
    }
  }, [filterData, showMobileFilters])

  const resetFilters = () => {
    setActiveFilters({})
    setCalendarValue(null)
  }

  const validOptions = (filter) => {
    if (filter.type === "dropdown" || filter.type === "multiselect") {
      return (
        filter.options &&
        filter.options?.length > 0 &&
        !filter.options?.some((op) => op === null)
      )
    }
    return true
  }

  const getFilterType = (filter) => {
    switch (filter.type) {
      case "dropdown":
        return (
          <Dropdown
            className={customTableStyles.filterItemBorder}
            defaultValue={filter.default ?? undefined}
            value={activeFilters[filter.value]}
            options={filter.options}
            placeholder={filter.placeholder}
            showClear={filter.showClear}
            onChange={(e) => {
              if (e.value === null || e.value === undefined) {
                // Remove the filter if the value is undefined
                const { [filter.value]: _, ...newFilters } = activeFilters
                setActiveFilters(newFilters)
              } else {
                // Set the filter value
                setActiveFilters({
                  ...activeFilters,
                  [filter.value]: e.value,
                })
              }
            }}
          />
        )
      case "calendar":
        const date = calendarValue
        return (
          <Calendar
            value={date}
            onChange={(e) => {
              if (e.target.value) {
                setCalendarValue(e.value) // Store the date in state for controlled input
                // Set the filter value
                setActiveFilters({
                  ...activeFilters,
                  [filter.value]: e.value.toISOString(),
                })
              }
            }}
            onClearButtonClick={() => {
              setCalendarValue(null) // Clear the date in state
              // Remove the filter if the value is undefined
              const { [filter.value]: _, ...newFilters } = activeFilters
              setActiveFilters(newFilters)
            }}
            dateFormat="yy-mm-dd"
            placeholder="mm/dd/yyyy"
            mask="99/99/9999"
            showIcon
            className={classNames(
              customTableStyles.calendar,
              customTableStyles.filterItemBorder,
            )}
            panelClassName="custom_calendar_panel"
            showButtonBar
          />
        )
      case "multiselect":
        return (
          <MultiSelect
            panelHeaderTemplate={!filter.filter ? <></> : undefined}
            display="chip"
            value={activeFilters[filter.value]}
            showSelectAll={filter.showSelectAll}
            options={filter.options}
            showClear={filter.showClear}
            placeholder={filter.placeholder}
            maxSelectedLabels={2}
            className={classNames(
              customTableStyles.multiselectFilter,
              customTableStyles.filterItemBorder,
            )}
            itemTemplate={multiselectFilterTemplate}
            filter={filter.filter}
            onChange={(e) => {
              if (e.value === undefined || e.value.length === 0) {
                // Remove the filter if the value is undefined
                const { [filter.value]: _, ...newFilters } = activeFilters
                setActiveFilters(newFilters)
              } else {
                // Set the filter value
                setActiveFilters({
                  ...activeFilters,
                  [filter.value]: e.value,
                })
              }
            }}
          />
        )
      case "search":
        return (
          <IconField
            iconPosition="left"
            className={customTableStyles.searchFilter}
          >
            <InputIcon className="pi pi-search"></InputIcon>
            <InputText
              value={activeFilters[filter.value] || ""}
              placeholder={filter.placeholder}
              onChange={(e) => {
                if (e.target.value.length > 0) {
                  // Update value in activeFilters
                  setActiveFilters({
                    ...activeFilters,
                    [filter.value]: e.target.value,
                  })
                } else {
                  // If the value is empty, remove it from activeFilters
                  const { [filter.value]: _, ...newFilters } = activeFilters
                  setActiveFilters(newFilters)
                }
              }}
            />
          </IconField>
        )
      default:
        break
    }
  }

  const multiselectFilterTemplate = (option) => {
    return (
      <div className={customTableStyles.filterTemplateItem}>
        <label className={customTableStyles.filterTemplateLabel}>
          {option}
        </label>
      </div>
    )
  }

  const sortTemplate = (option) => {
    const iconClass = activeSorting?.sortType?.[option.code]
      ? "pi pi-arrow-up"
      : "pi pi-arrow-down"
    return (
      <div className={customTableStyles.sortItem}>
        <label className={customTableStyles.sortLabel}>{option.name}</label>
        <i
          className={classNames(iconClass, customSortStyles.sortDirectionIcon)}
          onClick={(e) => {
            e.stopPropagation()
            setActiveSorting({
              ...activeSorting,
              sortType: {
                ...activeSorting.sortType,
                [option.code]: !activeSorting?.sortType?.[option.code],
              },
            })
          }}
        />
      </div>
    )
  }

  const getSortType = (sort) => {
    switch (sort.type) {
      case "multiselect":
        return (
          <MultiSelect
            display="chip"
            value={activeSorting[sort.value]}
            itemTemplate={sortTemplate}
            options={sort.options}
            panelHeaderTemplate={<></>}
            selectedItemTemplate={() => {
              return (
                <div className="flex flex-wrap w-full gap-2 items-center">
                  {getSelectedSortingItems()}
                </div>
              )
            }}
            className={
              "p-multiselect-sort " + customTableStyles.filterItemBorder
            }
            optionLabel="name"
            optionValue="code"
            onChange={(e) => {
              if (e.value.length === 0) return // Don't allow deselecting the last value

              let value = e.target.value
              // If singleSortOnly is true, only keep the last selected value
              if (singleSortOnly) {
                value = value.length > 0 ? [value[value.length - 1]] : []
              }
              setActiveSorting({
                ...activeSorting,
                [sort.value]: value,
              })
            }}
          />
        )
      default:
        break
    }
  }

  const getFilterItems = () => {
    return (
      <div
        className={`${customTableStyles.filterWrapper} ${customTableStyles.filterItem}`}
      >
        {filters?.map((filter) => {
          if (validOptions(filter)) {
            return (
              <div
                className={`${customTableStyles.headerItem} ${
                  filter.type === "search" && "w-20rem lg:w-30rem "
                }`}
                key={filter.label}
              >
                <label className={customTableStyles.headerLabel}>
                  {!filter.label ? "\u00A0" : filter.label}
                </label>
                {getFilterType(filter)}
              </div>
            )
          } else {
            return null
          }
        })}

        {showClearFilters && (
          <div className={customTableStyles.headerItem}>
            <label className={customTableStyles.headerLabel}>{"\u00A0"}</label>
            <Button
              onClick={() => {
                setActiveFilters({})
                setCalendarValue(null)
                setShowClearFilters(false)
              }}
              label="Reset Filters"
              icon="pi pi-times"
              className={customTableStyles.resetButton}
              type="button"
            />
          </div>
        )}
      </div>
    )
  }

  const getFilters = () => {
    return (
      <>
        {getFilterItems()}
        <div className={`${customTableStyles.filterWrapperMobile}`}>
          {filters && filters.length > 0 && (
            <Button
              label="Filters"
              onClick={() => {
                setShowMobileFilters(true)
              }}
              icon=" pi pi-sliders-h"
              iconPos="right"
              className={customTableStyles.filterMobile}
            />
          )}
          <div className="flex flex-wrap w-full gap-2">
            {Object.entries(activeFilters).map(([key, value]) => {
              let val = value
              if (DateTime.fromISO(val).isValid) {
                val = formatToLocalDate(val)
              }
              return (
                <div className={customTableStyles.filterMobileItem} key={key}>
                  <label>{val}</label>
                  <Button
                    icon="pi pi-times-circle"
                    className={customTableStyles.filterMobileItemIcon}
                    onClick={() => {
                      const propToRemove = key
                      const { [propToRemove]: _, ...newObj } = activeFilters
                      setActiveFilters(newObj)
                    }}
                  />
                </div>
              )
            })}
          </div>
        </div>
      </>
    )
  }

  const getSelectedSortingItems = () => {
    return activeSorting.sortBy?.map((s) => {
      const selectedOption = sorting
        .flatMap((item) => item?.options)
        ?.find((option) => option.code === s)
      const iconClass = activeSorting?.sortType?.[s]
        ? "pi pi-arrow-up"
        : "pi pi-arrow-down"
      return (
        <div className={customTableStyles.filterMobileItem} key={s}>
          <label>
            {`${selectedOption?.name}`}
            <i
              className={classNames(
                iconClass,
                customSortStyles.sortDirectionIconSmall,
              )}
            />
          </label>
          <Button
            icon="pi pi-times-circle"
            className={customTableStyles.filterMobileItemIcon}
            onClick={(e) => {
              e.stopPropagation()
              if (!activeSorting || !activeSorting.sortBy) return

              const updatedSorting = { ...activeSorting }

              // Ensure sortType is initialized
              updatedSorting.sortType = updatedSorting.sortType || {}

              const propToRemove = s

              // Remove property safely
              const { [propToRemove]: _, ...updatedSortType } =
                updatedSorting.sortType || {}

              updatedSorting.sortType = updatedSortType
              updatedSorting.sortBy =
                updatedSorting.sortBy?.filter(
                  (item) => item !== propToRemove,
                ) || []

              if (updatedSorting.sortBy.length === 0) {
                return // If no sorting is left, do not update the state
              }

              setActiveSorting(updatedSorting)
            }}
          />
        </div>
      )
    })
  }

  const getSorting = () => {
    return (
      <div className={customTableStyles.sortingWrapper}>
        {sorting?.map((item) => {
          return (
            <div
              className={`${customTableStyles.headerItem} ${customTableStyles.sortingItem}`}
              key={item.label}
            >
              <label className={customTableStyles.headerLabel}>
                {item.label}
              </label>
              {getSortType(item)}
            </div>
          )
        })}
      </div>
    )
  }

  const getItem = (item, header) => {
    if (header.template) {
      return header.template(item)
    }
    return item[header.value] ? item[header.value] : "\u00A0"
  }

  const getColumnSize = (size) => {
    switch (size) {
      case 1:
        return "col-12 md:col-4 lg:col-1"
      case 2:
        return "col-12 md:col-4 lg:col-2"
      case 3:
        return "col-12 md:col-4 lg:col-3"
      case 4:
        return "col-12 md:col-4 lg:col-4"
      case 5:
        return "col-12 md:col-4 lg:col-5"
      default:
        return "col-12 md:col-4 lg:col-2"
    }
  }

  const getData = () => {
    //Key counters
    let itemKeyCounter = 0
    let headerKeyCounter = 0
    let footerKeyCounter = 0

    const footer = headers.find((h) => h.footer)?.value

    return filteredData?.slice(first, first + rows).map((item) => {
      return (
        <div
          key={itemKeyCounter++}
          className={classNames(
            customTableStyles.tableRow,
            customTableStyles.tableRowBorder,
          )}
          style={tableRowStyles}
        >
          <div className={customTableStyles.rowData}>
            {headers
              .filter((h) => !h.footer)
              .map((header) => {
                return (
                  <div
                    key={headerKeyCounter++}
                    className={`${
                      customTableStyles.dataWrapper
                    } ${getColumnSize(header.size)} `}
                    style={header.action ? actionStyles : dataStyles}
                  >
                    {!header.custom && (
                      <label className={customTableStyles.dataHeader}>
                        {header.label}
                      </label>
                    )}
                    <label
                      className={
                        header.isActionColumn
                          ? customTableStyles.dataActionItem
                          : customTableStyles.dataItem
                      }
                    >
                      {getItem(item, header)}
                    </label>
                  </div>
                )
              })}
          </div>

          {footer && footer(item) && (
            <div
              key={footerKeyCounter++}
              className={customTableStyles.footerData}
            >
              {footer(item)}
            </div>
          )}
        </div>
      )
    })
  }

  const onPageChange = (event) => {
    setFirst(event.first)
    setRows(event.rows)
  }

  const getPaginator = () => {
    return (
      <div className={customTableStyles.paginator}>
        <Paginator
          first={first}
          rows={rows}
          totalRecords={filteredData?.length}
          onPageChange={onPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </div>
    )
  }

  const getNoData = () => {
    let title = "Nothing here... yet"
    let subTitle = "Data will be displayed here when available"
    if (filteredData.length === 0 && data.length > 0) {
      title = "No results found for the selected filters"
      subTitle = ""
    }
    return <NoData title={title} subTitle={subTitle} />
  }

  const getHeader = () => {
    if (!title && filters && sorting) {
      return (
        <div className={customTableStyles.headerWrapper}>
          {getFilters()}
          {getSorting()}
        </div>
      )
    }
    if (title && !filters && sorting) {
      return (
        <div className={customTableStyles.headerWrapper}>
          <span
            style={{
              color: "#383838",
              fontSize: "16px",
              fontWeight: "700",
            }}
          >
            {title}
          </span>
          {getSorting()}
        </div>
      )
    }
    if (title && filters && !sorting) {
      return (
        <div className={customTableStyles.headerWrapper}>
          <span
            style={{
              color: "#383838",
              fontSize: "16px",
              fontWeight: "700",
            }}
          >
            {title}
          </span>
          {getFilters()}
        </div>
      )
    }
  }

  return (
    <>
      <div className={customTableStyles.wrapper} style={styles}>
        {data.length > 0 && getHeader()}

        <div className={customTableStyles.customTable}>
          {filteredData?.length > 0 ? getData() : getNoData()}
        </div>
        {!disablePagination && getPaginator()}
      </div>
      <FilterDialogMobile
        filters={filters}
        getFilterType={getFilterType}
        setShowMobileFilters={setShowMobileFilters}
        showMobileFilters={showMobileFilters}
        resetFilters={resetFilters}
        applyFilters={filterData}
        validOptions={validOptions}
      />
    </>
  )
}
export default CustomTable
